// services/auth.js
import { supabase } from '../config/supabase';

class AuthService {
    constructor() {
        // SISTEMA MULTI-CREDENZIALI: Carica da Supabase DB, fallback su env
        // Quando una viene limitata (429), passa automaticamente alla successiva!
        this.spotifyCredentials = [];
        this.credentialsLoaded = false;
        this.credentialsLoading = false;

        // Tracking credenziali "bruciate" (rate limited)
        this.burnedCredentials = new Map(); // { id: resetTime }
        this.currentCredentialIndex = 0;

        // Credenziali per login utente (verrà impostata dopo il caricamento)
        this.clientId = null;
        this.clientSecret = null;
        
        this.redirectUri = process.env.REACT_APP_SPOTIFY_REDIRECT_URI;
        this.scopes = [
            'user-read-private',
            'user-read-email',
            'user-top-read',
            'user-library-read',
            'user-follow-read',  // Added for following artists
            'user-follow-modify', // Added for follow/unfollow actions
            'user-modify-playback-state',
            'streaming'
        ];

        // Log credenziali disponibili (lazy - solo quando necessario)
        this._initialized = false;

        this.environmentCredentials = this._loadEnvironmentCredentials();
    }

    /**
     * Carica le credenziali Spotify da Supabase (con fallback su hardcoded)
     */
    async loadCredentials() {
        // Evita caricamenti multipli simultanei
        if (this.credentialsLoading) {
            // Aspetta che il caricamento in corso finisca
            while (this.credentialsLoading) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }
            return;
        }

        if (this.credentialsLoaded) {
            return; // Già caricate
        }

        this.credentialsLoading = true;

        try {
            // Prova a caricare da Supabase
            const { data, error } = await supabase
                .from('spotify_credentials')
                .select('*')
                .eq('is_active', true)
                .order('created_at', { ascending: true });

            if (!error && data && data.length > 0) {
                // Converti i dati Supabase nel formato atteso
                this.spotifyCredentials = data.map((cred, index) => ({
                    id: cred.id,
                    clientId: cred.client_id,
                    clientSecret: cred.client_secret,
                    label: cred.label || `Spotify App ${index + 1}`
                }));

                console.log(`🔑 [Auth] Loaded ${this.spotifyCredentials.length} Spotify credentials from Supabase`);
            } else if (this.environmentCredentials.length > 0) {
                console.warn('⚠️ [Auth] Failed to load credentials from Supabase, using environment credentials');
                this.spotifyCredentials = [...this.environmentCredentials];
            } else {
                throw new Error('No Spotify credentials available (Supabase empty and no env credentials configured)');
            }
        } catch (error) {
            console.error('❌ [Auth] Error loading credentials:', error);
            if (this.environmentCredentials.length > 0) {
                console.warn('⚠️ [Auth] Falling back to environment credentials due to Supabase error');
                this.spotifyCredentials = [...this.environmentCredentials];
            } else {
                this.credentialsLoading = false;
                throw error;
            }
        }

        // Imposta credenziali per login utente (usa la prima)
        if (this.spotifyCredentials.length > 0) {
            this.clientId = this.spotifyCredentials[0].clientId;
            this.clientSecret = this.spotifyCredentials[0].clientSecret;
        }

        this.credentialsLoaded = true;
        this.credentialsLoading = false;
    }

    _logInit() {
        if (!this._initialized) {
            console.log(`🔑 [Auth] Initialized with ${this.spotifyCredentials.length} Spotify credentials sets`);
            this._initialized = true;
        }
    }

    async initiateLogin() {
        // Assicurati che le credenziali siano caricate
        await this.loadCredentials();

        // Pulisci eventuali dati residui
        this.clearAllData();
        
        // Genera e salva lo state
        const state = this.generateRandomString(16);
        localStorage.setItem('spotify_auth_state', state);

        // Costruisci l'URL di autorizzazione
        const authUrl = new URL('https://accounts.spotify.com/authorize');
        const params = {
            client_id: this.clientId,
            response_type: 'code',
            redirect_uri: this.redirectUri,
            state: state,
            scope: this.scopes.join(' '),
            show_dialog: true
        };

        authUrl.search = new URLSearchParams(params).toString();
        window.location.href = authUrl.toString();
    }

    async handleCallback(code, state) {
        const savedState = localStorage.getItem('spotify_auth_state');

        if (!savedState || state !== savedState) {
            console.error('State mismatch or missing');
            return false;
        }

        try {
            // Codifica le credenziali client in base64
            const basicAuth = btoa(`${this.clientId}:${this.clientSecret}`);

            // Prepara i parametri per la richiesta del token
            const params = new URLSearchParams({
                grant_type: 'authorization_code',
                code: code,
                redirect_uri: this.redirectUri
            });

            // Effettua la richiesta del token
            const response = await fetch('https://accounts.spotify.com/api/token', {
                method: 'POST',
                headers: {
                    'Authorization': `Basic ${basicAuth}`,
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Accept': 'application/json'
                },
                body: params
            });

            // Controlla la risposta
            if (!response.ok) {
                const errorData = await response.text();
                console.error('Token response error:', {
                    status: response.status,
                    statusText: response.statusText,
                    error: errorData
                });
                return false;
            }

            const data = await response.json();
            
            // Salva i token
            this.setTokens(data);
            
            // Pulisci lo state
            localStorage.removeItem('spotify_auth_state');
            
            return true;
        } catch (error) {
            console.error('Token request error:', error);
            return false;
        }
    }

    setTokens(data) {
        if (!data.access_token) {
            console.error('No access token in response');
            return;
        }

        const tokenData = {
            access_token: data.access_token,
            refresh_token: data.refresh_token,
            expires_at: Date.now() + (data.expires_in * 1000)
        };

        localStorage.setItem('spotify_tokens', JSON.stringify(tokenData));
    }

    /**
     * Ottiene la prossima credenziale disponibile (non "bruciata")
     */
    getNextAvailableCredential() {
        const now = Date.now();
        
        // Pulisci credenziali che sono state "sbloccate" (reset time passato)
        for (const [credId, resetTime] of this.burnedCredentials.entries()) {
            if (now >= resetTime) {
                console.log(`🔓 [Auth] Credential "${credId}" recovered from rate limit`);
                this.burnedCredentials.delete(credId);
            }
        }

        // Trova la prima credenziale non bruciata
        for (let i = 0; i < this.spotifyCredentials.length; i++) {
            const index = (this.currentCredentialIndex + i) % this.spotifyCredentials.length;
            const cred = this.spotifyCredentials[index];
            
            if (!this.burnedCredentials.has(cred.id)) {
                this.currentCredentialIndex = index;
                return cred;
            }
        }

        // Se TUTTE sono bruciate, ritorna quella che si sblocca prima
        console.warn('⚠️ [Auth] All credentials are rate limited! Using least burned one...');
        let minResetTime = Infinity;
        let bestCred = this.spotifyCredentials[0];
        
        for (const cred of this.spotifyCredentials) {
            const resetTime = this.burnedCredentials.get(cred.id) || 0;
            if (resetTime < minResetTime) {
                minResetTime = resetTime;
                bestCred = cred;
            }
        }
        
        return bestCred;
    }

    /**
     * Marca una credenziale come "bruciata" per X secondi
     */
    markCredentialAsBurned(credentialId, retryAfterSeconds = 300) {
        const resetTime = Date.now() + (retryAfterSeconds * 1000);
        this.burnedCredentials.set(credentialId, resetTime);
        console.warn(`🔥 [Auth] Credential "${credentialId}" marked as burned for ${retryAfterSeconds}s`);
    }

    /**
     * Ottiene un token Client Credentials con rotazione automatica
     */
    async getClientCredentialsToken(forceNewCredential = false, _retryCount = 0) {
        try {
            // Assicurati che le credenziali siano caricate
            await this.loadCredentials();
            
            // Log inizializzazione solo la prima volta
            this._logInit();
            
            // Limite massimo di tentativi per evitare loop infiniti
            if (_retryCount >= this.spotifyCredentials.length) {
                console.error('❌ [Auth] All credentials failed or are rate limited');
                return null;
            }
            
            // Se non forzato, controlla cache del token corrente
            if (!forceNewCredential) {
                const cachedToken = localStorage.getItem('spotify_client_token');
                if (cachedToken) {
                    try {
                        const tokenData = JSON.parse(cachedToken);
                        if (Date.now() < tokenData.expires_at) {
                            return tokenData.access_token;
                        }
                    } catch (e) {
                        // Ignora errori di parsing
                    }
                }
            }

            // Ottieni la prossima credenziale disponibile
            const credential = this.getNextAvailableCredential();
            
            console.log(`🔑 [Auth] Getting token using "${credential.label}" (${credential.id})...`);
            
            // Ottieni un nuovo token usando questa credenziale
            const basicAuth = btoa(`${credential.clientId}:${credential.clientSecret}`);
            
            const response = await fetch('https://accounts.spotify.com/api/token', {
                method: 'POST',
                headers: {
                    'Authorization': `Basic ${basicAuth}`,
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: 'grant_type=client_credentials'
            });

            if (!response.ok) {
                const errorText = await response.text();
                
                // Se è rate limiting, marca la credenziale come bruciata
                if (response.status === 429) {
                    const retryAfter = parseInt(response.headers.get('Retry-After') || '300', 10);
                    this.markCredentialAsBurned(credential.id, retryAfter);
                    
                    // Riprova con un'altra credenziale
                    console.log('🔄 [Auth] Trying next credential...');
                    return this.getClientCredentialsToken(true, _retryCount + 1);
                }
                
                console.error(`❌ [Auth] Token error for "${credential.label}":`, response.status, errorText);
                
                // Per altri errori (es. secret sbagliato), marca come bruciata per evitare tentativi ripetuti
                this.markCredentialAsBurned(credential.id, 60); // 1 minuto
                
                // Prova la prossima credenziale
                if (_retryCount < this.spotifyCredentials.length - 1) {
                    console.log('🔄 [Auth] Trying next credential due to error...');
                    return this.getClientCredentialsToken(true, _retryCount + 1);
                }
                
                return null;
            }

            const data = await response.json();
            
            // Salva il token in cache con riferimento alla credenziale
            const tokenData = {
                access_token: data.access_token,
                expires_at: Date.now() + (data.expires_in * 1000) - 60000, // -1 minuto di sicurezza
                credential_id: credential.id,
                credential_label: credential.label
            };
            
            try {
                localStorage.setItem('spotify_client_token', JSON.stringify(tokenData));
                console.log(`✅ [Auth] Token obtained from "${credential.label}"`);
            } catch (e) {
                // Se localStorage è pieno, pulisci e riprova
                console.warn('⚠️ localStorage quota exceeded, clearing old data...');
                try {
                    localStorage.removeItem('spotify_client_token');
                    localStorage.setItem('spotify_client_token', JSON.stringify(tokenData));
                } catch (err) {
                    console.error('❌ Cannot save token to localStorage:', err);
                    // Continua comunque, il token è in memoria
                }
            }
            
            return data.access_token;
        } catch (error) {
            console.error('Error getting client credentials token:', error);
            return null;
        }
    }

    /**
     * Notifica che una richiesta ha ricevuto 429 - invalida il token corrente
     */
    handleRateLimitError(retryAfterSeconds = 300) {
        try {
            const cachedToken = localStorage.getItem('spotify_client_token');
            if (cachedToken) {
                const tokenData = JSON.parse(cachedToken);
                if (tokenData.credential_id) {
                    this.markCredentialAsBurned(tokenData.credential_id, retryAfterSeconds);
                    
                    // Invalida il token corrente
                    localStorage.removeItem('spotify_client_token');
                    console.log('🔄 [Auth] Current token invalidated, will rotate to next credential');
                }
            }
        } catch (e) {
            console.warn('Could not handle rate limit error:', e);
        }
    }

    getToken() {
        try {
            // Prima prova token utente
            try {
                const userTokenData = JSON.parse(localStorage.getItem('spotify_tokens'));
                if (userTokenData && Date.now() < userTokenData.expires_at) {
                    return userTokenData.access_token;
                }
            } catch (e) {
                // Ignora errori di parsing/quota
            }
            
            // Poi prova token client credentials
            try {
                const clientTokenData = JSON.parse(localStorage.getItem('spotify_client_token'));
                if (clientTokenData && Date.now() < clientTokenData.expires_at) {
                    return clientTokenData.access_token;
                }
            } catch (e) {
                // Ignora errori di parsing/quota
            }
            
            // Se nessun token valido trovato
            console.log('📋 Nessun token valido trovato (user o client)');
            return null;
        } catch (error) {
            console.error('Errore parsing token:', error);
            return null;
        }
    }

    clearAllData() {
        localStorage.removeItem('spotify_tokens');
        localStorage.removeItem('spotify_auth_state');
    }

    isAuthenticated() {
        return !!this.getToken();
    }

    /**
     * Verifica se c'è un token UTENTE valido (non client credentials)
     * Utile per endpoint che richiedono autenticazione utente come /me
     */
    isUserAuthenticated() {
        try {
            const userTokenData = JSON.parse(localStorage.getItem('spotify_tokens'));
            if (userTokenData && Date.now() < userTokenData.expires_at) {
                return true;
            }
        } catch (e) {
            // Ignora errori di parsing/quota
        }
        return false;
    }

    logout({ redirectTo = null } = {}) {
        this.clearAllData();
        window.dispatchEvent(new Event('auth_logout'));
        if (redirectTo) {
            window.location.href = redirectTo;
        }
    }

    generateRandomString(length) {
        const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        return Array.from(crypto.getRandomValues(new Uint8Array(length)))
            .map(x => possible[x % possible.length])
            .join('');
    }

    _loadEnvironmentCredentials() {
        const envCredentials = [];

        const baseId = process.env.REACT_APP_SPOTIFY_CLIENT_ID || process.env.SPOTIFY_CLIENT_ID;
        const baseSecret = process.env.REACT_APP_SPOTIFY_CLIENT_SECRET || process.env.SPOTIFY_CLIENT_SECRET;
        if (baseId && baseSecret) {
            envCredentials.push({
                id: 'env_1',
                clientId: baseId,
                clientSecret: baseSecret,
                label: process.env.SPOTIFY_CREDENTIAL_LABEL || 'Spotify App (ENV)'
            });
        }

        for (let i = 2; i <= 5; i++) {
            const clientId = process.env[`REACT_APP_SPOTIFY_CLIENT_ID_${i}`] || process.env[`SPOTIFY_CLIENT_ID_${i}`];
            const clientSecret = process.env[`REACT_APP_SPOTIFY_CLIENT_SECRET_${i}`] || process.env[`SPOTIFY_CLIENT_SECRET_${i}`];
            if (clientId && clientSecret) {
                envCredentials.push({
                    id: `env_${i}`,
                    clientId,
                    clientSecret,
                    label: process.env[`SPOTIFY_CREDENTIAL_LABEL_${i}`] || `Spotify App ${i} (ENV)`
                });
            }
        }

        return envCredentials;
    }
}

export default new AuthService();